// Clean DP Reducer for Create DP Flow with Multiple Ship-To-Party Support
// Maintains single DP state with immutable order_booking_id and multiple ship-to-party carts

// Utility function to fix floating point precision issues
const roundToThreeDecimals = (num) => {
  return Math.round((num + Number.EPSILON) * 1000) / 1000;
};

// Utility function to calculate cart totals
const calculateCartTotals = (cart) => {
  const total_quantity_in_metric_ton = roundToThreeDecimals(
    cart.reduce((sum, item) => sum + item.quantity.quantity_in_metric_ton, 0)
  );
  
  const total_cart_value = roundToThreeDecimals(
    cart.reduce((sum, item) => 
      sum + (item.quantity.quantity_in_metric_ton * item.current_price_per_unit), 0
    )
  );
  
  return { total_quantity_in_metric_ton, total_cart_value };
};

// Initial state supporting multiple ship-to-party carts
const initialState = {
  order_booking_id: null,
  routeSource: null,
  
  // General cart (before ship-to-party selection)
  generalCart: [],
  generalCartTotals: {
    total_quantity_in_metric_ton: 0,
    total_cart_value: 0
  },
  
  // Multiple ship-to-party carts
  shipToPartyCarts: {
    // Structure: { [shipToPartyId]: { cart: [], totals: {}, shipToPartyDetails: {} } }
  },
  
  // Currently selected ship-to-party (null means general cart)
  selectedShipToPartyId: null,
  
  // DP details and metadata
  dpDetails: null,
  isLoading: false,
  error: null
};

const dpReducer = (state = initialState, action) => {
  switch (action.type) {
    case 'INIT_DP': {
      return {
        ...state,
        order_booking_id: state.order_booking_id || action.payload.order_booking_id,
        isLoading: false,
        error: null
      };
    }

    case 'ADD_PRODUCT': {
      const { product, shipToPartyId = null } = action.payload;
      
      if (shipToPartyId) {
        const currentShipToPartyCart = state.shipToPartyCarts[shipToPartyId] || {
          cart: [],
          totals: { total_quantity_in_metric_ton: 0, total_cart_value: 0 },
          shipToPartyDetails: null
        };
        
        const existingProductIndex = currentShipToPartyCart.cart.findIndex(
          item => item.product_id === product.product_id
        );

        let newCart;
        if (existingProductIndex >= 0) {
          newCart = currentShipToPartyCart.cart.map((item, index) => {
            if (index === existingProductIndex) {
              const newMetricTonQty = roundToThreeDecimals(
                item.quantity.quantity_in_metric_ton + product.quantity.quantity_in_metric_ton
              );
              return {
                ...item,
                quantity: {
                  ...item.quantity,
                  quantity_in_metric_ton: newMetricTonQty,
                  quantity_in_bags: newMetricTonQty * item.quantity.multiplier
                }
              };
            }
            return item;
          });
        } else {
          // Add new product
          newCart = [...currentShipToPartyCart.cart, {
            ...product,
            shipToPartyId: shipToPartyId
          }];
        }

        const newTotals = calculateCartTotals(newCart);

        return {
          ...state,
          shipToPartyCarts: {
            ...state.shipToPartyCarts,
            [shipToPartyId]: {
              ...currentShipToPartyCart,
              cart: newCart,
              totals: newTotals
            }
          }
        };
      } else {
        // Add to general cart
        const existingProductIndex = state.generalCart.findIndex(
          item => item.product_id === product.product_id
        );

        let newGeneralCart;
        if (existingProductIndex >= 0) {
          // Update existing product quantity
          newGeneralCart = state.generalCart.map((item, index) => {
            if (index === existingProductIndex) {
              const newMetricTonQty = roundToThreeDecimals(
                item.quantity.quantity_in_metric_ton + product.quantity.quantity_in_metric_ton
              );
              return {
                ...item,
                quantity: {
                  ...item.quantity,
                  quantity_in_metric_ton: newMetricTonQty,
                  quantity_in_bags: newMetricTonQty * item.quantity.multiplier
                }
              };
            }
            return item;
          });
        } else {
          newGeneralCart = [...state.generalCart, {
            ...product,
          }];
        }

        const newGeneralTotals = calculateCartTotals(newGeneralCart);

        return {
          ...state,
          generalCart: newGeneralCart,
          generalCartTotals: newGeneralTotals
        };
      }
    }

    case 'UPDATE_PRODUCT_QTY': {
      const { product_id, quantity_in_metric_ton, shipToPartyId = null } = action.payload;
      const newQuantity = roundToThreeDecimals(quantity_in_metric_ton);

      if (shipToPartyId) {
        // Update in specific ship-to-party cart
        const currentShipToPartyCart = state.shipToPartyCarts[shipToPartyId];
        if (!currentShipToPartyCart) return state;

        if (newQuantity <= 0) {
          // Remove product
          const newCart = currentShipToPartyCart.cart.filter(item => item.product_id !== product_id);
          const newTotals = calculateCartTotals(newCart);
          
          return {
            ...state,
            shipToPartyCarts: {
              ...state.shipToPartyCarts,
              [shipToPartyId]: {
                ...currentShipToPartyCart,
                cart: newCart,
                totals: newTotals
              }
            }
          };
        }

        const newCart = currentShipToPartyCart.cart.map(item => {
          if (item.product_id === product_id) {
            return {
              ...item,
              quantity: {
                ...item.quantity,
                quantity_in_metric_ton: newQuantity,
                quantity_in_bags: newQuantity * item.quantity.multiplier
              }
            };
          }
          return item;
        });

        const newTotals = calculateCartTotals(newCart);

        return {
          ...state,
          shipToPartyCarts: {
            ...state.shipToPartyCarts,
            [shipToPartyId]: {
              ...currentShipToPartyCart,
              cart: newCart,
              totals: newTotals
            }
          }
        };
      } else {
        // Update in general cart
        if (newQuantity <= 0) {
          // Remove product
          const newGeneralCart = state.generalCart.filter(item => item.product_id !== product_id);
          const newGeneralTotals = calculateCartTotals(newGeneralCart);
          
          return {
            ...state,
            generalCart: newGeneralCart,
            generalCartTotals: newGeneralTotals
          };
        }

        const newGeneralCart = state.generalCart.map(item => {
          if (item.product_id === product_id) {
            return {
              ...item,
              quantity: {
                ...item.quantity,
                quantity_in_metric_ton: newQuantity,
                quantity_in_bags: newQuantity * item.quantity.multiplier
              }
            };
          }
          return item;
        });

        const newGeneralTotals = calculateCartTotals(newGeneralCart);

        return {
          ...state,
          generalCart: newGeneralCart,
          generalCartTotals: newGeneralTotals
        };
      }
    }

    case 'REMOVE_PRODUCT': {
      const { product_id, shipToPartyId = null } = action.payload;

      if (shipToPartyId) {
        // Remove from specific ship-to-party cart
        const currentShipToPartyCart = state.shipToPartyCarts[shipToPartyId];
        if (!currentShipToPartyCart) return state;

        const newCart = currentShipToPartyCart.cart.filter(item => item.product_id !== product_id);
        const newTotals = calculateCartTotals(newCart);

        return {
          ...state,
          shipToPartyCarts: {
            ...state.shipToPartyCarts,
            [shipToPartyId]: {
              ...currentShipToPartyCart,
              cart: newCart,
              totals: newTotals
            }
          }
        };
      } else {
        // Remove from general cart
        const newGeneralCart = state.generalCart.filter(item => item.product_id !== product_id);
        const newGeneralTotals = calculateCartTotals(newGeneralCart);

        return {
          ...state,
          generalCart: newGeneralCart,
          generalCartTotals: newGeneralTotals
        };
      }
    }
    case 'SELECT_SHIP_TO_PARTY': {
      const shipToPartyId = action.payload.shipToPartyId;

      // If no general cart items, just select STP
      if (!shipToPartyId || state.generalCart.length === 0) {
        return {
          ...state,
          selectedShipToPartyId: shipToPartyId
        };
      }

      // 🔥 Move ALL general cart items into selected STP
      const currentShipToPartyCart =
        state.shipToPartyCarts[shipToPartyId] || {
          cart: [],
          totals: { total_quantity_in_metric_ton: 0, total_cart_value: 0 },
          shipToPartyDetails: null
        };

      const movedCart = [
        ...currentShipToPartyCart.cart,
        ...state.generalCart.map(item => ({
          ...item,
          shipToPartyId
        }))
      ];

      return {
        ...state,
        selectedShipToPartyId: shipToPartyId,
        generalCart: [],
        generalCartTotals: {
          total_quantity_in_metric_ton: 0,
          total_cart_value: 0
        },
        shipToPartyCarts: {
          ...state.shipToPartyCarts,
          [shipToPartyId]: {
            ...currentShipToPartyCart,
            cart: movedCart,
            totals: calculateCartTotals(movedCart)
          }
        }
      };
    }
    case 'SET_SHIP_TO_PARTY_DETAILS': {
      const { shipToPartyId, shipToPartyDetails } = action.payload;
      
      // Initialize ship-to-party cart if it doesn't exist
      const currentShipToPartyCart = state.shipToPartyCarts[shipToPartyId] || {
        cart: [],
        totals: { total_quantity_in_metric_ton: 0, total_cart_value: 0 },
        shipToPartyDetails: null
      };

      return {
        ...state,
        shipToPartyCarts: {
          ...state.shipToPartyCarts,
          [shipToPartyId]: {
            ...currentShipToPartyCart,
            shipToPartyDetails: shipToPartyDetails
          }
        }
      };
    }

    case 'MOVE_PRODUCTS_TO_SHIP_TO_PARTY': {
      // Move products from general cart to specific ship-to-party cart
      const { shipToPartyId, productIds } = action.payload;
      
      const productsToMove = state.generalCart.filter(item => 
        productIds.includes(item.product_id)
      );
      
      const remainingGeneralCart = state.generalCart.filter(item => 
        !productIds.includes(item.product_id)
      );

      const currentShipToPartyCart = state.shipToPartyCarts[shipToPartyId] || {
        cart: [],
        totals: { total_quantity_in_metric_ton: 0, total_cart_value: 0 },
        shipToPartyDetails: null
      };

      const newShipToPartyCart = [
        ...currentShipToPartyCart.cart,
        ...productsToMove.map(product => ({ ...product, shipToPartyId }))
      ];

      const newGeneralTotals = calculateCartTotals(remainingGeneralCart);
      const newShipToPartyTotals = calculateCartTotals(newShipToPartyCart);

      return {
        ...state,
        generalCart: remainingGeneralCart,
        generalCartTotals: newGeneralTotals,
        shipToPartyCarts: {
          ...state.shipToPartyCarts,
          [shipToPartyId]: {
            ...currentShipToPartyCart,
            cart: newShipToPartyCart,
            totals: newShipToPartyTotals
          }
        }
      };
    }

    case 'SET_DP_DETAILS': {
      return {
        ...state,
        dpDetails: action.payload.dpDetails
      };
    }
    case 'REMOVE_SHIP_TO_PARTY': {
      const id = action.payload?.id ?? action.data?.id;
      if (!id) return state;

      const carts = { ...state.shipToPartyCarts };
      delete carts[id];

      return {
        ...state,
        shipToPartyCarts: carts,
        selectedShipToPartyId: null,
      };
    }

    case 'SET_LOADING': {
      return {
        ...state,
        isLoading: action.payload.isLoading
      };
    }
    case 'UPDATE_SHIP_TO_PARTY_PRODUCT_PRICE': {
      const { shipToPartyId, productId, newPrice } = action.data;

      const currentShipToPartyCart = state.shipToPartyCarts?.[shipToPartyId];
      if (!currentShipToPartyCart) return state;

      let priceChanged = false;

      const updatedCart = currentShipToPartyCart.cart.map(item => {
        if (item.product_id === productId && item.current_price_per_unit !== newPrice) {
          priceChanged = true;
          return {
            ...item,
            current_price_per_unit: newPrice
          };
        }
        return item;
      });

      // ⛔ Avoid unnecessary re-render if nothing changed
      if (!priceChanged) return state;

      const newTotals = calculateCartTotals(updatedCart);

      return {
        ...state,
        shipToPartyCarts: {
          ...state.shipToPartyCarts,
          [shipToPartyId]: {
            ...currentShipToPartyCart,
            cart: updatedCart,
            totals: newTotals
          }
        }
      };
    }

    case 'SET_ERROR': {
      return {
        ...state,
        error: action.payload.error,
        isLoading: false
      };
    }
    case 'RESET_SELECTED_SHIP_TO_PARTY':{
      return {
        ...state,
        selectedShipToPartyId: null
      };
    }
    case 'HYDRATE_DP_FROM_ORDER': {
      const { orderBookingId, shipToPartyCarts } = action.payload;

      return {
        ...state,
        order_booking_id: orderBookingId,
        generalCart: [],
        generalCartTotals: {
          total_quantity_in_metric_ton: 0,
          total_cart_value: 0
        },
        shipToPartyCarts,
        selectedShipToPartyId: Object.keys(shipToPartyCarts)[0] || null,
        isLoading: false,
        error: null
      };
    }
    case 'RESET_DP': {
      return {
        ...initialState
      };
    }
    case 'SET_DP_ROUTE_SOURCE':
      return {
        ...state,
        routeSource: action.payload,
      };

    case 'RESET_DP_ROUTE_SOURCE':
      return {
        ...state,
        routeSource: null,
      };

    default:
      return state;
  }
};

export default dpReducer;

// Enhanced Action Creators for Multiple Ship-To-Party Support
export const dpActions = {
  // Initialize DP with order_booking_id (immutable once set)
  initDP: (order_booking_id) => ({
    type: 'INIT_DP',
    payload: { order_booking_id }
  }),

  // Add product to general cart or specific ship-to-party cart
  addProduct: (product, shipToPartyId = null) => ({
    type: 'ADD_PRODUCT',
    payload: { product, shipToPartyId }
  }),

  // Update product quantity in general cart or specific ship-to-party cart
  updateProductQty: (product_id, quantity_in_metric_ton, shipToPartyId = null) => ({
    type: 'UPDATE_PRODUCT_QTY',
    payload: { product_id, quantity_in_metric_ton, shipToPartyId }
  }),

  hydrateDpFromOrder: (payload) => ({
    type: 'HYDRATE_DP_FROM_ORDER',
    payload
  }),

  // Remove product from general cart or specific ship-to-party cart
  removeProduct: (product_id, shipToPartyId = null) => ({
    type: 'REMOVE_PRODUCT',
    payload: { product_id, shipToPartyId }
  }),

  // Select which ship-to-party cart is currently active (null for general cart)
  selectShipToParty: (shipToPartyId) => ({
    type: 'SELECT_SHIP_TO_PARTY',
    payload: { shipToPartyId }
  }),

  // Set ship-to-party details
  setShipToPartyDetails: (shipToPartyId, shipToPartyDetails) => ({
    type: 'SET_SHIP_TO_PARTY_DETAILS',
    payload: { shipToPartyId, shipToPartyDetails }
  }),

  // Move products from general cart to specific ship-to-party cart
  moveProductsToShipToParty: (shipToPartyId, productIds) => ({
    type: 'MOVE_PRODUCTS_TO_SHIP_TO_PARTY',
    payload: { shipToPartyId, productIds }
  }),

  // Set DP details
  setDPDetails: (dpDetails) => ({
    type: 'SET_DP_DETAILS',
    payload: { dpDetails }
  }),

  // Set loading state
  setLoading: (isLoading) => ({
    type: 'SET_LOADING',
    payload: { isLoading }
  }),

  // Set error
  setError: (error) => ({
    type: 'SET_ERROR',
    payload: { error }
  }),

  // Reset DP state
  resetDP: () => ({
    type: 'RESET_DP'
  })
};

// Enhanced Selectors for Multiple Ship-To-Party Support
export const dpSelectors = {
  // Basic selectors
  selectOrderBookingId: (state) => state.dpReducer?.order_booking_id,
  selectSelectedShipToPartyId: (state) => state.dpReducer?.selectedShipToPartyId,
  selectIsLoading: (state) => state.dpReducer?.isLoading || false,
  selectError: (state) => state.dpReducer?.error,
  selectDPDetails: (state) => state.dpReducer?.dpDetails,
  
  // General cart selectors
  selectGeneralCart: (state) => state.dpReducer?.generalCart || [],
  selectGeneralCartTotals: (state) => state.dpReducer?.generalCartTotals || { total_quantity_in_metric_ton: 0, total_cart_value: 0 },
  selectGeneralCartQuantity: (state) => state.dpReducer?.generalCartTotals?.total_quantity_in_metric_ton || 0,
  selectGeneralCartValue: (state) => state.dpReducer?.generalCartTotals?.total_cart_value || 0,
  
  // Ship-to-party selectors
  selectShipToPartyCarts: (state) => state.dpReducer?.shipToPartyCarts || {},
  selectShipToPartyCart: (state, shipToPartyId) => 
    state.dpReducer?.shipToPartyCarts?.[shipToPartyId]?.cart || [],
  selectShipToPartyTotals: (state, shipToPartyId) => 
    state.dpReducer?.shipToPartyCarts?.[shipToPartyId]?.totals || { total_quantity_in_metric_ton: 0, total_cart_value: 0 },
  selectShipToPartyDetails: (state, shipToPartyId) => 
    state.dpReducer?.shipToPartyCarts?.[shipToPartyId]?.shipToPartyDetails,
  
  // Current active cart selectors (based on selectedShipToPartyId)
  selectCurrentCart: (state) => {
    const selectedShipToPartyId = state.dpReducer?.selectedShipToPartyId;
    if (selectedShipToPartyId) {
      return state.dpReducer?.shipToPartyCarts?.[selectedShipToPartyId]?.cart || [];
    }
    return state.dpReducer?.generalCart || [];
  },
  
  selectCurrentCartTotals: (state) => {
    const selectedShipToPartyId = state.dpReducer?.selectedShipToPartyId;
    if (selectedShipToPartyId) {
      return state.dpReducer?.shipToPartyCarts?.[selectedShipToPartyId]?.totals || { total_quantity_in_metric_ton: 0, total_cart_value: 0 };
    }
    return state.dpReducer?.generalCartTotals || { total_quantity_in_metric_ton: 0, total_cart_value: 0 };
  },
  
  // Utility selectors
  selectCartItemQty: (state, product_id, shipToPartyId = null) => {
    let cart;
    if (shipToPartyId) {
      cart = state.dpReducer?.shipToPartyCarts?.[shipToPartyId]?.cart || [];
    } else {
      const selectedShipToPartyId = state.dpReducer?.selectedShipToPartyId;
      cart = selectedShipToPartyId 
        ? state.dpReducer?.shipToPartyCarts?.[selectedShipToPartyId]?.cart || []
        : state.dpReducer?.generalCart || [];
    }
    
    const item = cart.find(item => item.product_id === product_id);
    return item ? item.quantity.quantity_in_metric_ton : 0;
  },
  
  selectAllShipToPartyIds: (state) => 
    Object.keys(state.dpReducer?.shipToPartyCarts || {}),
  
  selectTotalDPQuantity: (state) => {
    const generalTotal = state.dpReducer?.generalCartTotals?.total_quantity_in_metric_ton || 0;
    const shipToPartyTotals = Object.values(state.dpReducer?.shipToPartyCarts || {})
      .reduce((sum, cart) => sum + (cart.totals?.total_quantity_in_metric_ton || 0), 0);
    return roundToThreeDecimals(generalTotal + shipToPartyTotals);
  },
  
  selectTotalDPValue: (state) => {
    const generalTotal = state.dpReducer?.generalCartTotals?.total_cart_value || 0;
    const shipToPartyTotals = Object.values(state.dpReducer?.shipToPartyCarts || {})
      .reduce((sum, cart) => sum + (cart.totals?.total_cart_value || 0), 0);
    return roundToThreeDecimals(generalTotal + shipToPartyTotals);
  }
};

/*
ENHANCED USAGE EXAMPLES FOR MULTIPLE SHIP-TO-PARTY:

// 1. Initialize DP (from PO List screen)
dispatch(dpActions.initDP(12345)); // order_booking_id = 12345

// 2. Add products to general cart (before ship-to-party selection)
dispatch(dpActions.addProduct(productObject)); // Goes to general cart

// 3. Create ship-to-party and set details
dispatch(dpActions.setShipToPartyDetails('STP001', {
  id: 'STP001',
  name: 'ABC Construction',
  address: '123 Main St'
}));

// 4. Move products from general cart to ship-to-party cart
dispatch(dpActions.moveProductsToShipToParty('STP001', [227, 229])); // Move products 227, 229

// 5. Switch to ship-to-party context for adding more products
dispatch(dpActions.selectShipToParty('STP001'));
dispatch(dpActions.addProduct(newProduct, 'STP001')); // Add directly to STP001

// 6. Add products to different ship-to-party
dispatch(dpActions.addProduct(anotherProduct, 'STP002'));

// 7. Update quantities for specific ship-to-party
dispatch(dpActions.updateProductQty(227, 5, 'STP001'));

// 8. Remove product from specific ship-to-party
dispatch(dpActions.removeProduct(229, 'STP001'));

// COMPONENT USAGE:
const currentCart = useSelector(dpSelectors.selectCurrentCart);
const currentTotals = useSelector(dpSelectors.selectCurrentCartTotals);
const shipToPartyCart = useSelector(state => dpSelectors.selectShipToPartyCart(state, 'STP001'));
const allShipToParties = useSelector(dpSelectors.selectAllShipToPartyIds);
const totalDPValue = useSelector(dpSelectors.selectTotalDPValue);

// COMPLETE DP STRUCTURE:
{
  order_booking_id: 12345,
  generalCart: [...], // Products before ship-to-party assignment
  shipToPartyCarts: {
    'STP001': {
      cart: [...], // Products for ABC Construction
      totals: { total_quantity_in_metric_ton: 10, total_cart_value: 500000 },
      shipToPartyDetails: { id: 'STP001', name: 'ABC Construction' }
    },
    'STP002': {
      cart: [...], // Products for XYZ Builder
      totals: { total_quantity_in_metric_ton: 5, total_cart_value: 250000 },
      shipToPartyDetails: { id: 'STP002', name: 'XYZ Builder' }
    }
  },
  selectedShipToPartyId: 'STP001' // Currently viewing STP001's cart
}

*/
